import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AI_PHOTOSHOOTING__BASE_PATH,
  AVATAR_PATH,
  LARGE_IMAGE_PATH,
  SMALL_IMAGE_PATH,
  TESTTASK_ID,
  TEST_IMAGE_ID,
} from 'utils/configs';

import handleError from 'services/handleError';
import createTaskFromGallery from 'services/createTaskFromGallery';
import createTaskFromUploaded from 'services/createTaskFromUploaded';
import getTaskList from 'services/getTaskList';
import goToTheTask from 'services/goToTheTask';
import deleteTask from 'services/deleteTask';
import sendFeedback from 'services/sendFeedback';
import processAvatarImages from 'services/processAvatarImages';
import getCachedImageBlobUrl from 'services/getCachedImageBlobUrl';

import defaultInputImage from 'media/images/test_input.jpg';
import defaultResultImage from 'media/images/test_result.jpg';

const defaultTask = {
  id: TESTTASK_ID,
  avatar: { image: '07966_00.jpeg' },
  data: [
    {
      id: TEST_IMAGE_ID,
      input_image: defaultInputImage,
      input_image_small: defaultInputImage,
      input_image_large: defaultInputImage,
      results: {
        images: [
          {
            id: TEST_IMAGE_ID,
            result_image: defaultResultImage,
            result_image_basic: defaultResultImage,
            result_image_large: defaultResultImage,
          },
        ],
      },
    },
  ],

  status: 'test',
};

function getBaseUrl(id) {
  return `${AI_PHOTOSHOOTING__BASE_PATH}/task/${id}/image/`;
}

function getBaseAvatarUrl() {
  return `${AI_PHOTOSHOOTING__BASE_PATH}${AVATAR_PATH}`;
}

const processImages = async dataArray => {
  return await Promise.all(
    dataArray.map(async item => {
      const avatarBlob = await getCachedImageBlobUrl(
        getBaseAvatarUrl,
        'null',
        item.avatar.image
      );

      const mediaWithBlobs = await Promise.all(
        item.data.map(async el => ({
          ...el,
          input_image: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            el.input_image
          ),
          input_image_large: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            el.input_image,
            LARGE_IMAGE_PATH
          ),
          input_image_small: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            el.input_image,
            SMALL_IMAGE_PATH
          ),

          results: {
            images: await Promise.all(
              el.results.images.map(async elem => ({
                ...elem,
                result_image: elem.result_image,
                result_image_basic: await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  elem.result_image,
                  ''
                ),
                result_image_large: await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  elem.result_image,
                  LARGE_IMAGE_PATH
                ),
              }))
            ),
            video: el.results.video
              ? {
                  ...el.results.video,
                  result_video: el.results.video.result_video,
                  result_video_basic: await getCachedImageBlobUrl(
                    getBaseUrl,
                    item.id,
                    el.results.video.result_video,
                    ''
                  ),
                  result_image_basic: await getCachedImageBlobUrl(
                    getBaseUrl,
                    item.id,
                    el.results.video.result_image,
                    ''
                  ),
                  result_image_large: await getCachedImageBlobUrl(
                    getBaseUrl,
                    item.id,
                    el.results.video.result_image,
                    LARGE_IMAGE_PATH
                  ),
                }
              : null,
          },
        }))
      );

      return {
        ...item,
        avatar: { ...item.avatar, image: avatarBlob },
        data: mediaWithBlobs,
      };
    })
  );
};

export const getAvatars = createAsyncThunk(
  'aiphotoshooting/getAvatars',

  async (_, thunkAPI) => {
    try {
      const { data } = await axios.get(
        `${AI_PHOTOSHOOTING__BASE_PATH}/avatar/query
        `
      );

      const processedData = await processAvatarImages(data); // Обробка всіх зображень

      return processedData;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const createTask = createAsyncThunk(
  'aiphotoshooting/create',

  async (taskData, thunkAPI) => {
    try {
      const formData = new FormData();

      Object.entries(taskData).forEach(([key, value]) => {
        if (key !== 'input_files') {
          formData.append(key, value);
        } else {
          value.forEach((item, index) => {
            formData.append('input_files', value[index]);
          });
        }
      });

      const data = await createTaskFromUploaded(
        AI_PHOTOSHOOTING__BASE_PATH,
        formData,
        thunkAPI
      );

      return data;
    } catch (error) {
      console.error('❌ Error of creating:', error);
    }
  }
);

export const createAiPhotoshootingTaskFromGallery = createAsyncThunk(
  'aiphotoshooting/createFromGallery',

  async (taskData, thunkAPI) => {
    try {
      const data = await createTaskFromGallery(
        AI_PHOTOSHOOTING__BASE_PATH,
        taskData,
        thunkAPI
      );

      return data;
    } catch (error) {
      console.error('❌ Error of creating:', error);
    }
  }
);

export const getAiPhotoshootingTaskList = createAsyncThunk(
  'aiphotoshooting/gallery',

  async (_, thunkAPI) => {
    try {
      let processedData = [];
      const data = await getTaskList(AI_PHOTOSHOOTING__BASE_PATH, thunkAPI);

      if (data.length > 0) {
        processedData = await processImages(data); // Обробка всіх зображень
      } else {
        const avatarBlob = await getCachedImageBlobUrl(
          getBaseAvatarUrl,
          'null',
          defaultTask.avatar.image
        );
        processedData = [{ ...defaultTask, avatar: { image: avatarBlob } }];
      }

      return processedData;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const goToTheAiPhotoshootingTask = createAsyncThunk(
  'aiphotoshooting/goToTheTask',

  async (taskId, thunkAPI) => {
    let processedData = [];
    try {
      if (taskId === TESTTASK_ID) {
        const avatarBlob = await getCachedImageBlobUrl(
          getBaseAvatarUrl,
          'null',
          defaultTask.avatar.image
        ); // Processing all images

        processedData = [{ ...defaultTask, avatar: { image: avatarBlob } }];
      } else {
        const data = await goToTheTask(
          AI_PHOTOSHOOTING__BASE_PATH,
          taskId,
          thunkAPI
        );

        processedData = await processImages([data]); // Processing all images
      }

      return processedData[0];
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const removeTask = createAsyncThunk(
  'aiphotoshooting/removeTask',

  async (taskId, thunkAPI) => {
    try {
      const data = deleteTask(AI_PHOTOSHOOTING__BASE_PATH, taskId, thunkAPI);

      return data;
    } catch (error) {
      console.error('❌ Error deleting:', error);
    }
  }
);

export const sendAiPhotoshootingImageFeedback = createAsyncThunk(
  'aiphotoshooting/imageFeedback',

  async (feedbackBody, thunkAPI) => {
    try {
      const { value, taskId, mediaId, mediaType } = feedbackBody;
      const toolPath = `${AI_PHOTOSHOOTING__BASE_PATH}/task`;

      let responce = null;

      if (mediaType === 'image') {
        responce = await sendFeedback(
          value,
          toolPath,
          taskId,
          mediaId,
          thunkAPI
        );
      } else if (mediaType === 'video') {
        await axios.post(`${toolPath}/${taskId}/video/${mediaId}/feedback`, {
          value,
        });

        responce = { mediaId, value };
      }
      return responce;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);
